<script setup lang="ts">
import { dateTimeFormatter } from '@/helpers/ep/formatters'
import { getClaimHistory } from '@/services/asicw/claims.service'
import type { IClaimHistoryFilters } from '@/types/asicw/claims'
import { OrderBy } from '@/types/shared/filters'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import { Icon } from '@iconify/vue'
import { useQuery } from '@tanstack/vue-query'
import { DateTime } from 'luxon'
import { computed, reactive, ref } from 'vue'

const open = defineModel<boolean>()
const props = defineProps<{ claimNumber: string }>()

const dateRangeFilter = ref<[Date, Date] | undefined>()

// Compute message to display when table is empty.
const tableEmptyText = computed(() => {
  return isFetching.value ? 'Loading...' : 'No records'
})

const filters = reactive<IClaimHistoryFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[0],
  order_by: 'id',
  order_by_order: OrderBy.DESC,
  start_date: undefined,
  end_date: undefined,
})

const { isFetching, isPending, isError, isRefetching, data, error, refetch } = useQuery({
  queryKey: ['claim-history', props.claimNumber, filters],
  queryFn: () => getClaimHistory(props.claimNumber, filters),
  placeholderData: (prev: any) => prev,
})

/**
 * Handle `el-datepicker` range change.
 *
 * @param newRange the new selected date range.
 */
const handleDateRangeChange = async (newRange: Array<Date> | null) => {
  if (!newRange) {
    filters.start_date = undefined
    filters.end_date = undefined
    return
  }

  filters.start_date = DateTime.fromJSDate(newRange[0]).toISO() ?? undefined
  filters.end_date = DateTime.fromJSDate(newRange[1]).toISO() ?? undefined
}
</script>

<template>
  <el-dialog v-model="open" :width="1000" title="Claim History" :show-close="false">
    <div class="flex flex-col gap-8">
      <rl-error-result v-if="isError && !isRefetching" :error="error">
        <el-button @click="() => refetch()">
          <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
        </el-button>
      </rl-error-result>
      <template v-else>
        <div class="inline-flex justify-between">
          <div class="flex gap-2">
            <el-date-picker
              v-model="dateRangeFilter"
              :disabled="isPending"
              type="datetimerange"
              range-separator="to"
              start-placeholder="Start date"
              end-placeholder="End date"
              format="YYYY-MM-DD HH:mm:ss"
              date-format="YYYY/MM/DD ddd"
              time-format="A hh:mm:ss"
              editable
              clearable
              @change="handleDateRangeChange"
            />
          </div>
        </div>
        <el-table v-loading="isFetching" :height="500" :data="data?.data" :empty-text="tableEmptyText" stripe>
          <el-table-column prop="action" label="Action" :min-width="312" />
          <el-table-column prop="action_user" label="User" :min-width="188" />
          <el-table-column prop="action_timestamp" :formatter="dateTimeFormatter" label="Time" :min-width="188" />
        </el-table>
        <el-pagination
          v-if="data && data.total_pages > 0"
          v-model:current-page="filters.page"
          v-model:page-size="filters.per_page"
          class="self-end"
          :page-sizes="DEFAULT_PAGE_SIZES"
          :page-count="data.total_pages"
          :total="data.total_records"
          :disabled="isFetching"
          :layout="DEFAULT_PAGINATION_LAYOUT"
          background
        />
      </template>
    </div>
  </el-dialog>
</template>
